import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  EmptyState,
  ErrorState,
  Field,
  FormError,
  Input,
  LoadingState,
  PageHeader,
  Select,
  Textarea,
  useToast,
} from '@lawbee/ui';
import { Check, Code, Copy, ExternalLink, FileText, Plus } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../../../lib/api';
import { useCopy } from '../../../lib/hooks';
import type { IntakeFormDetail, IntakeFormSummary, IntakePreset } from '../../../lib/types';

export function IntakeFormsPage() {
  const q = useQuery({ queryKey: ['intake-forms'], queryFn: () => api.get<{ forms: IntakeFormSummary[] }>('/api/intake/forms') });
  const [creating, setCreating] = useState(false);
  const [embedFor, setEmbedFor] = useState<IntakeFormSummary | null>(null);
  const forms = q.data?.forms ?? [];

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Link to="/intakes" className="hover:text-foreground">
            Intakes
          </Link>
        }
        title="Intake forms"
        description="Public forms prospects fill in before a consultation. Link to them or embed them on your website."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden /> New form
          </Button>
        }
      />

      {q.isLoading ? (
        <LoadingState label="Loading forms" />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : forms.length === 0 ? (
        <EmptyState
          icon={<FileText />}
          title="No intake forms yet"
          description="Start from a practice-area preset and adjust the questions to fit your firm."
          action={
            <Button onClick={() => setCreating(true)}>
              <Plus aria-hidden /> New form
            </Button>
          }
        />
      ) : (
        <ul className="grid gap-4 md:grid-cols-2">
          {forms.map((f) => (
            <FormCard key={f.id} form={f} onEmbed={() => setEmbedFor(f)} />
          ))}
        </ul>
      )}

      <NewFormDialog open={creating} onOpenChange={setCreating} />
      <EmbedDialog form={embedFor} onOpenChange={(o) => !o && setEmbedFor(null)} />
    </div>
  );
}

function FormCard({ form, onEmbed }: { form: IntakeFormSummary; onEmbed: () => void }) {
  const { copy, copied } = useCopy();
  return (
    <li className="flex flex-col gap-4 rounded-xl bg-card p-5 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to="/intake-forms/$id" params={{ id: form.id }} className="text-[17px] font-semibold tracking-[-0.01em] hover:underline">
            {form.name}
          </Link>
          <p className="text-[13px] text-muted-foreground">{form.matterTypeName ?? 'No matter type'}</p>
        </div>
        {form.active ? <Badge variant="success">Active</Badge> : <Badge>Inactive</Badge>}
      </div>
      <div className="flex gap-6 text-sm">
        <div>
          <div className="figures text-lg font-semibold">{form.fieldCount}</div>
          <div className="text-[12px] text-muted-foreground">{form.fieldCount === 1 ? 'question' : 'questions'}</div>
        </div>
        <div>
          <div className="figures text-lg font-semibold">{form.intakeCount}</div>
          <div className="text-[12px] text-muted-foreground">{form.intakeCount === 1 ? 'intake' : 'intakes'}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2">
        <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-muted-foreground" title={form.publicUrl}>
          {form.publicUrl}
        </span>
        <Button size="sm" variant="ghost" className="h-7 px-2" aria-label={`Copy link to ${form.name}`} onClick={() => void copy(form.publicUrl, 'Link copied')}>
          {copied === form.publicUrl ? <Check aria-hidden /> : <Copy aria-hidden />}
        </Button>
        <Button asChild size="sm" variant="ghost" className="h-7 px-2">
          <a href={form.publicUrl} target="_blank" rel="noreferrer" aria-label={`Open ${form.name} in a new tab`}>
            <ExternalLink aria-hidden />
          </a>
        </Button>
      </div>
      <div className="mt-auto flex flex-wrap gap-2">
        <Button asChild size="sm">
          <Link to="/intake-forms/$id" params={{ id: form.id }}>
            Edit form
          </Link>
        </Button>
        <Button size="sm" variant="outline" onClick={onEmbed}>
          <Code aria-hidden /> Embed
        </Button>
      </div>
    </li>
  );
}

function EmbedDialog({ form, onOpenChange }: { form: IntakeFormSummary | null; onOpenChange: (open: boolean) => void }) {
  const { copy } = useCopy();
  const detail = useQuery({
    queryKey: ['intake-form', form?.id],
    queryFn: () => api.get<{ form: IntakeFormDetail }>(`/api/intake/forms/${form!.id}`),
    enabled: !!form,
  });
  const fallback = form ? `<iframe src="${form.publicUrl}?embed=1" title="${form.name.replace(/"/g, '')}" style="width:100%;min-height:900px;border:0"></iframe>` : '';
  const code = detail.data?.form.embedCode ?? form?.embedCode ?? fallback;
  return (
    <Dialog open={!!form} onOpenChange={onOpenChange}>
      <DialogContent
        size="lg"
        title="Embed on your website"
        description="Paste this code into any page of your site where the form should appear."
        footer={
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <Button onClick={() => void copy(code, 'Embed code copied')}>
              <Copy aria-hidden /> Copy code
            </Button>
          </>
        }
      >
        <Field label="Embed code" htmlFor="embed-code">
          <Textarea id="embed-code" readOnly rows={5} value={code} className="font-mono text-[12px]" onFocus={(e) => e.currentTarget.select()} />
        </Field>
      </DialogContent>
    </Dialog>
  );
}

function NewFormDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const presets = useQuery({ queryKey: ['intake-presets'], queryFn: () => api.get<{ presets: IntakePreset[] }>('/api/intake/presets'), enabled: open, staleTime: Infinity });
  const [name, setName] = useState('');
  const [preset, setPreset] = useState('');
  const create = useMutation({
    mutationFn: () => api.post<{ form: IntakeFormDetail }>('/api/intake/forms', { name: name.trim(), ...(preset ? { preset } : { fields: [] }) }),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['intake-forms'] });
      toast.success('Form created');
      onOpenChange(false);
      setName('');
      setPreset('');
      void navigate({ to: '/intake-forms/$id', params: { id: res.form.id } });
    },
  });
  const choosePreset = (value: string) => {
    setPreset(value);
    const p = presets.data?.presets.find((x) => x.area === value);
    if (p && !name.trim()) setName(p.name);
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) create.reset();
      }}
    >
      <DialogContent
        title="New intake form"
        footer={
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" form="new-form" loading={create.isPending} disabled={name.trim().length < 2}>
              Create and edit
            </Button>
          </>
        }
      >
        <form
          id="new-form"
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim().length >= 2) create.mutate();
          }}
        >
          <Field label="Start from" htmlFor="form-preset" description="Presets include the questions lawyers in that area usually ask, including a parties list for the conflict check.">
            <Select
              id="form-preset"
              value={preset}
              onChange={(e) => choosePreset(e.target.value)}
              disabled={presets.isLoading}
              options={[{ value: '', label: 'Blank form (contact details only)' }, ...(presets.data?.presets ?? []).map((p) => ({ value: p.area, label: p.label }))]}
            />
          </Field>
          <Field label="Form name" htmlFor="form-name" required>
            <Input id="form-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Family law intake" autoFocus />
          </Field>
          <FormError message={create.error ? errorMessage(create.error) : presets.error ? errorMessage(presets.error) : null} />
        </form>
      </DialogContent>
    </Dialog>
  );
}
